import { normalizeDateToIso } from './dateNormalization';
import { buildQuoteFundingFields } from './quoteFundingPayload';

/**
 * @param {*} value
 * @returns {string}
 */
function str(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * @param {*} value
 * @returns {string}
 */
function priceStr(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return str(value);
  return String(n);
}

/**
 * Deep clone extraction draft for editable review state.
 *
 * @param {import('../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationData} data
 * @returns {import('../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationData}
 */
export function cloneExtractedOrderConfirmationData(data) {
  return JSON.parse(JSON.stringify(data || {}));
}

/**
 * Sum line item prices (line totals) for order confirmation total price.
 *
 * @param {import('../types/orderConfirmationExtractionTypes').ExtractedOrderItem[]} items
 * @returns {number}
 */
export function sumOrderConfirmationLineTotals(items) {
  return (items || []).reduce((sum, item) => {
    const n = Number(item?.lineTotal);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/**
 * @returns {import('../types/orderConfirmationExtractionTypes').ExtractedOrderItem}
 */
export function emptyExtractedOrderItem() {
  return {
    productNumber: null,
    description: null,
    quantity: null,
    lineTotal: null,
    currency: null,
    rawRow: null,
  };
}

/**
 * Map reviewed extraction draft to POST submit-reviewed payload.
 *
 * @param {import('../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationData} data
 * @param {Object} context
 * @param {number|string} context.supplierId
 * @param {number|null} [context.projectId]
 * @param {number|null} [context.userId]
 * @param {string} [context.budget]
 * @param {string|number|null} [context.fundingCreditId]
 * @param {{ formatId: string, displayName: string }|null} [context.resolvedFormat]
 * @param {{ value: unknown, label: string, matchReason?: string }|null} [context.suggestedSupplier]
 * @returns {import('../types/orderConfirmationExtractionTypes').SubmitReviewedOrderConfirmationPayload}
 */
export function mapExtractedOrderConfirmationToSubmitPayload(data, context) {
  const headerCurrency = str(data.order?.currency);
  const orderDateIso =
    normalizeDateToIso(data.order?.orderDate) || str(data.order?.orderDate);

  const totalPrice =
    data.totals?.itemsSubtotal != null
      ? Number(data.totals.itemsSubtotal)
      : sumOrderConfirmationLineTotals(data.items);

  const quoteItems = (data.items || []).map(item => {
    const currency = str(item.currency) || headerCurrency;
    const lineTotal = item.lineTotal != null ? Number(item.lineTotal) : null;
    return {
      supplier_catalog_number: str(item.productNumber),
      product_name: str(item.description),
      quantity: item.quantity != null ? Number(item.quantity) : 0,
      price: priceStr(item.lineTotal),
      currency,
      line_total: lineTotal,
    };
  });

  const fundingResult = buildQuoteFundingFields({
    budgetLineId: context.budgetLineId,
    budget: str(context.budget),
    fundingCreditId: context.fundingCreditId,
    items: quoteItems.map(row => ({
      quantity: row.quantity,
      price: row.price,
      discount: '',
    })),
  });
  if (!fundingResult.ok) {
    throw new Error(fundingResult.error);
  }

  const extractionMeta = {
    ...(data.meta || {}),
    orderDateIso,
    supplierSnapshot: data.supplier,
    orderHeaderSnapshot: data.order,
    totalsSnapshot: data.totals,
    matchedSupplierLabel: context.suggestedSupplier?.label ?? null,
    matchReason: context.suggestedSupplier?.matchReason ?? null,
    resolvedFormatDisplayName: context.resolvedFormat?.displayName ?? null,
    parser_format: data.meta?.parserFormat ?? null,
  };

  const payload = {
    supplier: Number(context.supplierId),
    budget: fundingResult.fields.budget ?? null,
    budget_id: fundingResult.fields.budget_id ?? null,
    project: context.projectId != null ? Number(context.projectId) : null,
    user: context.userId != null ? Number(context.userId) : null,
    order_status: 'sent_to_supplier',
    order_confirmation: {
      supplier_name: str(data.supplier?.name),
      order_number: str(data.order?.orderNumber),
      purchase_order_number: null,
      order_date: orderDateIso,
      total: totalPrice,
      currency: headerCurrency,
      vat_amount: null,
      vat_rate: null,
      items_subtotal: totalPrice,
      extraction_meta: extractionMeta,
    },
    quote_items: quoteItems,
  };

  if (fundingResult.fields.funding_credit_id != null) {
    payload.funding_credit_id = fundingResult.fields.funding_credit_id;
    payload.funding_amount = fundingResult.fields.funding_amount;
  }

  return payload;
}
